/** @jsxImportSource preact */
import * as copy from "../paraglide/messages.js";
import { CARD_STYLES } from "./form/card-styles";
import { FORM_STYLES, formVars } from "./form/form-styles";
import { InfoIcon } from "./icons";
import type { WidgetSizeCard } from "./message";
import { SR_ONLY } from "./sr-only";
import { SERIF_META_FONT, type WidgetTokens } from "./theme";

interface SizeCardProps {
  card: WidgetSizeCard;
  tokens: WidgetTokens;
  /** 入场动画：跟着刚流完的回复出来时才有，时刻由消息排。 */
  animation?: string;
}

/**
 * 尺码卡的专属规则，叠在 CARD_STYLES 之上。
 * Editorial 不套卡片：去掉底、阴影、圆角和内边距，只留上下两条细线，井也压平成一条分隔线，
 * 视觉权重不超过消息本身。
 */
export const SIZE_CARD_STYLES = `
.gn-size-card { max-width:320px; }
.gn-size-head { align-items:flex-end; }
.gn-size-main { min-width:0; display:flex; flex-direction:column; gap:2px; }
.gn-size-label { font-family:var(--gn-meta-font,inherit); }
.gn-size-value { font-size:34px; font-weight:var(--gn-size-weight,800); letter-spacing:-.03em; line-height:1; overflow-wrap:anywhere; }
.gn-size-head .gn-chip { flex:none; font-family:var(--gn-meta-font,inherit); }
.gn-size-grid { display:grid; grid-template-columns:repeat(var(--gn-size-cols,3), minmax(0, 1fr)); gap:8px 12px; margin:0; }
.gn-size-grid > div { min-width:0; display:flex; flex-direction:column; gap:2px; }
.gn-size-grid dt { font-family:var(--gn-meta-font,inherit); font-size:12px; font-weight:var(--gn-form-strong); color:var(--gn-form-quiet); }
.gn-size-grid dd { margin:0; font-size:13px; font-weight:700; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
.gn-size-source { display:flex; align-items:center; gap:6px; padding:0 4px; font-family:var(--gn-meta-font,inherit); font-size:12px; color:var(--gn-form-quiet); }
.gn-size-source > svg { color:var(--gn-form-faint); }
.gn-size-source > span { min-width:0; overflow-wrap:anywhere; }
.gn-size-card[data-editorial] { gap:0; padding:0; border-top:1px solid var(--gn-form-line); border-bottom:1px solid var(--gn-form-line); border-radius:0; background:transparent; box-shadow:none; }
.gn-size-card[data-editorial] .gn-size-head { padding:10px 0; }
.gn-size-card[data-editorial] .gn-size-value { font-size:24px; letter-spacing:-.01em; }
.gn-size-card[data-editorial] .gn-well { padding:9px 0; border-top:1px solid var(--gn-form-line); border-radius:0; background:transparent; }
.gn-size-card[data-editorial] .gn-size-source { padding:8px 0; border-top:1px solid var(--gn-form-line); }
`;

/**
 * 尺码卡（PRD 2.4）：左边「Recommended size」标签加大号推荐码，右上是对应美码胶囊；
 * 三围放进内凹的井里排成一行，最后一行是来源。
 * 为什么推荐这个码写在正文气泡里，卡上不重复；数据里没有的（美码、三围、来源）就整块不画。
 */
export function SizeCard({ card, tokens: t, animation }: SizeCardProps) {
  const cols = Math.min(card.measurements.length, 3);
  return (
    <section
      class="gn-card gn-size-card"
      aria-label={copy.widget_size_recommended()}
      data-editorial={t.editorial || undefined}
      style={{
        ...formVars(t),
        "--gn-meta-font": t.serif ? SERIF_META_FONT : "inherit",
        "--gn-size-weight": t.serif ? 400 : 800,
        animation,
      }}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${SIZE_CARD_STYLES}`}</style>
      <div class="gn-card-head gn-size-head">
        <div class="gn-size-main">
          <div class="gn-caption gn-size-label" aria-hidden="true">
            {copy.widget_size_recommended()}
          </div>
          <div class="gn-size-value">{card.recommendedSize}</div>
        </div>
        {card.usSize !== undefined && card.usSize !== "" && (
          <span class="gn-chip">
            {copy.widget_size_us({ size: card.usSize })}
          </span>
        )}
      </div>
      {cols > 0 && (
        <div class="gn-well" data-roomy>
          <dl class="gn-size-grid" style={{ "--gn-size-cols": cols }}>
            {card.measurements.map((item, index) => (
              <div key={`${String(index)}:${item.label}`}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {card.source !== undefined && card.source !== "" && (
        <div class="gn-size-source">
          <InfoIcon size={13} />
          <span>
            <span style={SR_ONLY}>{copy.widget_size_source()} </span>
            {card.source}
          </span>
        </div>
      )}
    </section>
  );
}
